import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { discardDraft, fetchPendingProjects, submitProject } from '../api/projects.api';
import { queryKeys } from '@/shared/lib/queryKeys';
import { invalidateAfterTransition } from '@/shared/lib/invalidate';
import type { Id, ProjectWithContext } from '@/shared/types/domain';

/**
 * "O que está parado esperando você" (US-16, US-21 · decisão 49): rascunho e
 * projeto devolvido para ajuste, na mesma lista.
 *
 * ⚠️ **Sem `enabled`**: o único chamador é `/rascunhos`, e lá a consulta é a
 * tela. Ele existia para a sidebar, que carregava o contador de "Minhas
 * pendências" em toda sessão de quem tem `writeDraft` — o item saiu do menu, e
 * com ele a única chamada que precisava ser condicional.
 */
export function usePendingProjects(): UseQueryResult<ProjectWithContext[]> {
  return useQuery({
    queryKey: queryKeys.projects.pending,
    queryFn: fetchPendingProjects,
    staleTime: 30_000,
  });
}

interface TransitionTarget {
  projectId: Id;
  ministryId: Id;
}

/**
 * Enviar para análise.
 *
 * `invalidateAfterTransition` é obrigatório (regra 10, sem exceção): o projeto
 * some de "rascunhos", entra na fila de análise e passa a contar no painel do
 * ministério, tudo na mesma ação. Cache velho aqui produz o pior bug possível
 * deste painel — a pessoa envia, não vê mudar, e envia de novo.
 */
export function useSubmitProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      comment,
    }: TransitionTarget & { comment?: string | null }) =>
      submitProject(projectId, comment),
    onSuccess: (_data, variables) =>
      invalidateAfterTransition(queryClient, variables.projectId, variables.ministryId),
  });
}

/** Descartar rascunho: soft delete por RPC, nunca `DELETE` do cliente. */
export function useDiscardDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId }: TransitionTarget) => discardDraft(projectId),
    onSuccess: (_data, variables) =>
      invalidateAfterTransition(queryClient, variables.projectId, variables.ministryId),
  });
}
